import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  App,
  InputNumber,
} from "antd";

import dayjs from "../../../config/dayjs";
import {
  clientCategoryLabels,
  fileBillType,
  fileClientCategory,
  fileClientType,
  fileType,
  clientTypeLabels,
  fileTypeLabels,
  billTypeLabels,
} from "../../../constant/enum";
import { getBranchList } from "../../../general/api/branch.api";
import { capitalizeEachWord } from "../../../utils/stringUtils";
import { getUsersByRole, getUserById } from "../../user/api/userAPI";
import { createFile, editFile } from "../api/fileAPI";
import { IFileRequest, FileRequestSchema } from "../type/file";

interface FileFormProps {
  initialData?: IFileRequest;
  mode?: "create" | "update";
  fileId?: string;
}

export const FileForm = ({
  initialData,
  mode = "create",
  fileId,
}: FileFormProps) => {
  const navigate = useNavigate();
  const { message: messageApi } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ================ Branch Data ================ */
  const { data: branchData } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranchList,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const branchOptions =
    branchData?.results.map((branch: { id: string; branchName: string }) => ({
      value: branch.id,
      label: branch.branchName,
    })) || [];

  const initialBranchId = initialData?.branchId;
  /* ================ End Branch Data ================ */

  /* ================ Agent Data ================ */
  const [searchAgentText, setSearchAgentText] = useState("");
  const initialAgentId = initialData?.agentId;

  const { data: agentData, isFetching: isAgentLoading } = useQuery({
    queryKey: ["agents", searchAgentText],
    queryFn: () =>
      getUsersByRole("agent", {
        ...(searchAgentText && { name: searchAgentText }),
        limit: 10,
        page: 1,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get initial agent data if it exists
  const { data: initialAgentData } = useQuery({
    queryKey: ["user", initialAgentId],
    queryFn: () => getUserById(initialAgentId!),
    enabled: !!initialAgentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const agentOptions = [
    ...(agentData?.results.map((agent: { id: string; name: string }) => ({
      value: agent.id,
      label: capitalizeEachWord(agent.name),
    })) || []),
    // Include initial agent if it exists and not in current results
    ...(initialAgentData &&
    !agentData?.results.some((a) => a.id === initialAgentData.id)
      ? [
          {
            value: initialAgentId,
            label: capitalizeEachWord(initialAgentData.name),
          },
        ]
      : []),
  ];
  /* ================ End Agent Data ================ */

  /* ================ Client Data ================ */
  const [searchClientText, setSearchClientText] = useState("");
  const initialClientId = initialData?.clientId;

  const { data: clientData, isFetching: isClientLoading } = useQuery({
    queryKey: ["users", searchClientText],
    queryFn: () =>
      getUsersByRole("client", {
        ...(searchClientText && { name: searchClientText }),
        limit: 10,
        page: 1,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get initial client data if it exists
  const { data: initialClientData } = useQuery({
    queryKey: ["user", initialClientId],
    queryFn: () => getUserById(initialClientId!),
    enabled: !!initialClientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const clientOptions = [
    ...(clientData?.results.map((client: { id: string; name: string }) => ({
      value: client.id,
      label: capitalizeEachWord(client.name),
    })) || []),
    // Include initial client if it exists and not in current results
    ...(initialClientData &&
    !clientData?.results.some((c) => c.id === initialClientData.id)
      ? [
          {
            value: initialClientId,
            label: capitalizeEachWord(initialClientData.name),
          },
        ]
      : []),
  ];
  /* ================ End Client Data ================ */

  /* ================ Purchaser Data ================ */
  const [searchPurchaserText, setSearchPurchaserText] = useState("");

  const initialPurchaserId = initialData?.purchaserId;

  const { data: purchaserData, isFetching: isPurchaserLoading } = useQuery({
    queryKey: ["users", searchPurchaserText],
    queryFn: () =>
      getUsersByRole("client", {
        ...(searchPurchaserText && { name: searchPurchaserText }),
        limit: 10,
        page: 1,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get initial purchaser data if it exists
  const { data: initialPurchaserData } = useQuery({
    queryKey: ["user", initialPurchaserId],
    queryFn: () => getUserById(initialPurchaserId!),
    enabled: !!initialPurchaserId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const purchaserOptions = [
    ...(purchaserData?.results.map(
      (purchaser: { id: string; name: string }) => ({
        value: purchaser.id,
        label: capitalizeEachWord(purchaser.name),
      })
    ) || []),
    // Include initial purchaser if it exists and not in current results
    ...(initialPurchaserData &&
    !purchaserData?.results.some((p) => p.id === initialPurchaserData.id)
      ? [
          {
            value: initialPurchaserData.id,
            label: capitalizeEachWord(initialPurchaserData.name),
          },
        ]
      : []),
  ];
  /* ================ End Purchaser Data ================ */

  /* ================ Vendor Data ================ */
  const [searchVendorText, setSearchVendorText] = useState("");
  const initialVendorId = initialData?.vendorId;

  const { data: vendorData, isFetching: isVendorLoading } = useQuery({
    queryKey: ["users", searchVendorText],
    queryFn: () =>
      getUsersByRole("client", {
        ...(searchVendorText && { name: searchVendorText }),
        limit: 10,
        page: 1,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get initial vendor data if it exists
  const { data: initialVendorData } = useQuery({
    queryKey: ["user", initialVendorId],
    queryFn: () => getUserById(initialVendorId!),
    enabled: !!initialVendorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const vendorOptions = [
    ...(vendorData?.results.map((vendor: { id: string; name: string }) => ({
      value: vendor.id,
      label: capitalizeEachWord(vendor.name),
    })) || []),
    // Include initial vendor if it exists and not in current results
    ...(initialVendorData &&
    !vendorData?.results.some((v) => v.id === initialVendorData.id)
      ? [
          {
            value: initialVendorId,
            label: capitalizeEachWord(initialVendorData.name),
          },
        ]
      : []),
  ];
  /* ================ End Vendor Data ================ */

  const clientTypeOptions = fileClientType.map((type) => ({
    value: type,
    label: clientTypeLabels[type],
  }));

  const fileTypeOptions = fileType.map((type) => ({
    value: type,
    label: fileTypeLabels[type],
  }));

  const billTypeOptions = fileBillType.map((type) => ({
    value: type,
    label: billTypeLabels[type],
  }));

  const clientCategoryOptions = fileClientCategory.map((type) => ({
    value: type,
    label: clientCategoryLabels[type],
  }));

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<IFileRequest>({
    resolver: zodResolver(FileRequestSchema),
    defaultValues: {
      ...initialData,
      branchId: initialBranchId,
      agentId: initialAgentId,
      clientId: initialClientId,
      purchaserId: initialPurchaserId,
    },
  });

  const fileTypeValue = watch("fileType");

  const onSubmit = async (data: IFileRequest) => {
    setIsSubmitting(true);
    try {
      if (mode === "create") {
        await createFile(data);
        messageApi.success("Successfully file created!");
      } else {
        if (fileId) {
          await editFile(fileId, data);
          messageApi.success("Successfully file updated!");
        }
      }
      navigate("/files");
    } catch (error: any) {
      messageApi.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Row gutter={36}>
        {/* ================ Reference Number Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Reference Number"
            name="refNumber"
            hasFeedback
            validateStatus={errors.refNumber ? "error" : ""}
            help={errors.refNumber?.message}
            required
          >
            <Controller
              name="refNumber"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your reference number"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Client Type Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Client Type"
            name="clientType"
            hasFeedback
            validateStatus={errors.clientType ? "error" : ""}
            help={errors.clientType?.message}
            required
          >
            <Controller
              name="clientType"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Client Type"
                  {...field}
                  size="large"
                  options={clientTypeOptions}
                  allowClear
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ File Type Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="File Type"
            name="fileType"
            hasFeedback
            validateStatus={errors.fileType ? "error" : ""}
            help={errors.fileType?.message}
            required
          >
            <Controller
              name="fileType"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select File Type"
                  {...field}
                  size="large"
                  options={fileTypeOptions}
                  allowClear
                />
              )}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="File Type Remark"
            name="fileTypeRemark"
            hasFeedback
            validateStatus={errors.fileTypeRemark ? "error" : ""}
            help={errors.fileTypeRemark?.message}
          >
            <Controller
              name="fileTypeRemark"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your file type remark"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* Only show these fields if fileType is not litigation */}
        {fileTypeValue !== "litigation" && (
          <>
            {/* ================ Bill Type Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Bill Type"
                name="billType"
                hasFeedback
                validateStatus={errors.billType ? "error" : ""}
                help={errors.billType?.message}
                required
              >
                <Controller
                  name="billType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Bill Type"
                      {...field}
                      size="large"
                      options={billTypeOptions}
                      allowClear
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Bill Type Remark"
                name="billTypeRemark"
                hasFeedback
                validateStatus={errors.billTypeRemark ? "error" : ""}
                help={errors.billTypeRemark?.message}
              >
                <Controller
                  name="billTypeRemark"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your bill type remark"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Property Address Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Property Address"
                name="propertyAddress"
                hasFeedback
                validateStatus={errors.propertyAddress ? "error" : ""}
                help={errors.propertyAddress?.message}
                required
              >
                <Controller
                  name="propertyAddress"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your property address"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ SPA Price Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="SPA/Loan/Finance/Tenancy/Contract Amount"
                name="spaPrice"
                hasFeedback
                validateStatus={errors.spaPrice ? "error" : ""}
                help={errors.spaPrice?.message}
                required
              >
                <Controller
                  name="spaPrice"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      placeholder="Please enter your property value"
                      {...field}
                      size="large"
                      style={{
                        width: "100%",
                        borderColor: errors.spaPrice ? "#ff4d4f" : "#A6A6A6",
                      }}
                      formatter={(value) =>
                        value
                          ? value
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          : ""
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {/* ================ Branch Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Branch"
            name="branchId"
            hasFeedback
            validateStatus={errors.branchId ? "error" : ""}
            help={errors.branchId?.message}
            required
          >
            <Controller
              name="branchId"
              control={control}
              defaultValue={initialBranchId}
              render={({ field }) => {
                return (
                  <Select
                    {...field}
                    placeholder="Select Branch"
                    size="large"
                    options={branchOptions}
                    allowClear
                  />
                );
              }}
            />
          </Form.Item>
        </Col>

        {/* Only show these fields if fileType is not litigation */}
        {fileTypeValue !== "litigation" && (
          <>
            {/* ================ Client Category Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Client Category"
                name="clientCategory"
                hasFeedback
                validateStatus={errors.clientCategory ? "error" : ""}
                help={errors.clientCategory?.message}
                required={fileTypeValue !== "litigation"}
              >
                <Controller
                  name="clientCategory"
                  control={control}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Client Category"
                      {...field}
                      size="large"
                      options={clientCategoryOptions}
                      allowClear
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Client Category Remark"
                name="clientCategoryRemark"
                hasFeedback
                validateStatus={errors.clientCategoryRemark ? "error" : ""}
                help={errors.clientCategoryRemark?.message}
              >
                <Controller
                  name="clientCategoryRemark"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your client category remark"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {/* Only show these fields if fileType is litigation */}
        {fileTypeValue === "litigation" && (
          <>
            {/* ================ Client Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Client"
                name="clientId"
                hasFeedback
                validateStatus={errors.clientId ? "error" : ""}
                help={errors.clientId?.message}
                required={fileTypeValue === "litigation"}
              >
                <Controller
                  name="clientId"
                  control={control}
                  defaultValue={initialClientId}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Client"
                      {...field}
                      size="large"
                      showSearch
                      filterOption={false}
                      loading={isClientLoading}
                      onSearch={(value) => {
                        setSearchClientText(value);
                      }}
                      options={clientOptions}
                      allowClear
                      notFoundContent={
                        isClientLoading
                          ? "Loading..."
                          : searchClientText
                          ? "No clients found"
                          : clientOptions.length === 0
                          ? "No clients available"
                          : null
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {/* Only show these field if fileType is not litigation */}
        {fileTypeValue !== "litigation" && (
          <>
            {/* ================ Purchaser Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Purchaser"
                name="purchaserId"
                hasFeedback
                validateStatus={errors.purchaserId ? "error" : ""}
                help={errors.purchaserId?.message}
              >
                <Controller
                  name="purchaserId"
                  control={control}
                  defaultValue={initialPurchaserId}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Purchaser"
                      {...field}
                      size="large"
                      showSearch
                      filterOption={false}
                      loading={isPurchaserLoading}
                      onSearch={(value) => {
                        setSearchPurchaserText(value);
                      }}
                      options={purchaserOptions}
                      allowClear
                      notFoundContent={
                        isPurchaserLoading
                          ? "Loading..."
                          : searchPurchaserText
                          ? "No purchasers found"
                          : purchaserOptions.length === 0
                          ? "No purchasers available"
                          : null
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Vendor Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Vendor"
                name="vendorId"
                hasFeedback
                validateStatus={errors.vendorId ? "error" : ""}
                help={errors.vendorId?.message}
              >
                <Controller
                  name="vendorId"
                  control={control}
                  defaultValue={initialVendorId}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Vendor"
                      {...field}
                      size="large"
                      showSearch
                      filterOption={false}
                      loading={isVendorLoading}
                      onSearch={(value) => {
                        setSearchVendorText(value);
                      }}
                      options={vendorOptions}
                      allowClear
                      notFoundContent={
                        isVendorLoading
                          ? "Loading..."
                          : searchVendorText
                          ? "No vendors found"
                          : vendorOptions.length === 0
                          ? "No vendors available"
                          : null
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Agent Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Agent"
                name="agentId"
                hasFeedback
                validateStatus={errors.agentId ? "error" : ""}
                help={errors.agentId?.message}
                required
              >
                <Controller
                  name="agentId"
                  control={control}
                  defaultValue={initialAgentId}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Agent"
                      {...field}
                      size="large"
                      showSearch
                      filterOption={false}
                      loading={isAgentLoading}
                      onSearch={(value) => {
                        setSearchAgentText(value);
                      }}
                      options={agentOptions}
                      allowClear
                      notFoundContent={
                        isAgentLoading
                          ? "Loading..."
                          : searchAgentText
                          ? "No agents found"
                          : agentOptions.length === 0
                          ? "No agents available"
                          : null
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ PIC Name Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="PIC Name"
                name="picName"
                hasFeedback
                validateStatus={errors.picName ? "error" : ""}
                help={errors.picName?.message}
              >
                <Controller
                  name="picName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter PIC name"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {/* ================ Compliance Date Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Target Completion Date"
            name="completionDate"
            hasFeedback
            validateStatus={errors.completionDate ? "error" : ""}
            help={errors.completionDate?.message}
          >
            <Controller
              name="completionDate"
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <DatePicker
                  style={{
                    width: "100%",
                    borderColor: errors.completionDate ? "#ff4d4f" : "#A6A6A6",
                  }}
                  placeholder="Please enter compliance date"
                  value={value ? dayjs(value) : null}
                  onChange={(date) => onChange(date ? date.toDate() : null)}
                  {...restField}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ File Remark Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="File Remark"
            name="fileRemark"
            hasFeedback
            validateStatus={errors.fileRemark ? "error" : ""}
            help={errors.fileRemark?.message}
            required={fileTypeValue === "litigation"}
          >
            <Controller
              name="fileRemark"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your file remark"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* ================ Form Actions ================ */}
      <Form.Item>
        <Row justify="end">
          <Space
            direction="horizontal"
            wrap
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              style={{ margin: 5 }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ margin: 5 }}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {mode === "create" ? "Create File" : "Update File"}
            </Button>
          </Space>
        </Row>
      </Form.Item>
    </Form>
  );
};
